import { useState } from 'react';
import { PRESETS, periodLabel, usePeriod } from './PeriodContext';
import { useDismiss } from './useDismiss';

/**
 * Seletor de período da barra superior — RF-DAS-03.
 *
 * Vale para todas as telas e SOBREVIVE À NAVEGAÇÃO. Antes, cada tela tinha o
 * seu: escolher junho em Lançamentos e ir ao DRE devolvia agosto, e comparar
 * dois relatórios do mesmo período exigia acertar a data duas vezes.
 */
export function PeriodSelector() {
  const { period, setPeriod, applyPreset } = usePeriod();
  const [open, setOpen] = useState(false);
  const box = useDismiss<HTMLDivElement>(open, () => setOpen(false));

  function custom(from: string, to: string) {
    // Fim antes do início devolveria intervalo vazio sem explicar por quê.
    if (from > to) return;

    setPeriod({ from, to, preset: 'personalizado' });
  }

  return (
    <div className="relative" ref={box}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium transition hover:border-slate-400 dark:border-slate-700 dark:bg-slate-950"
        title={`${period.from} a ${period.to}`}
      >
        <span className="opacity-50">◷</span>
        <span className="max-w-[10rem] truncate">{periodLabel(period)}</span>
        <span className="text-xs opacity-50">▾</span>
      </button>

      {open && (
        <div className="absolute left-0 top-full z-20 mt-1.5 w-72 rounded-xl border border-slate-200 bg-white p-1.5 shadow-lg dark:border-slate-800 dark:bg-slate-900">
          {PRESETS.map((preset) => (
            <button
              key={preset.id}
              onClick={() => {
                applyPreset(preset.id);
                setOpen(false);
              }}
              className={`flex w-full items-center rounded-lg px-3 py-2 text-left text-sm ${
                period.preset === preset.id
                  ? 'bg-indigo-50 font-medium text-indigo-700 dark:bg-indigo-950/50 dark:text-indigo-300'
                  : 'hover:bg-slate-100 dark:hover:bg-slate-800'
              }`}
            >
              {preset.label}
            </button>
          ))}

          <div className="my-1.5 border-t border-slate-100 dark:border-slate-800" />

          {/* Os dois campos ficam EMPILHADOS, não lado a lado.
              Campo de data nativo tem largura mínima própria — "dd/mm/aaaa"
              mais o ícone do calendário — e o navegador não a reduz. Dois
              deles na mesma linha estouram a caixa em qualquer largura
              razoável de menu, e o limite muda com navegador e idioma. */}
          <div className="space-y-1.5 px-3 pb-1">
            <p className="text-xs font-medium text-slate-500 dark:text-slate-400">
              Período personalizado
            </p>

            <label className="flex items-center gap-2">
              <span className="w-7 shrink-0 text-xs text-slate-400">De</span>
              <input
                type="date"
                value={period.from}
                onChange={(e) => custom(e.target.value, period.to)}
                className="input min-w-0 flex-1 py-1 text-xs"
                aria-label="Início do período"
              />
            </label>

            <label className="flex items-center gap-2">
              <span className="w-7 shrink-0 text-xs text-slate-400">Até</span>
              <input
                type="date"
                value={period.to}
                // Impede escolher um fim anterior ao início pelo próprio
                // calendário, em vez de aceitar e devolver lista vazia.
                min={period.from}
                onChange={(e) => custom(period.from, e.target.value)}
                className="input min-w-0 flex-1 py-1 text-xs"
                aria-label="Fim do período"
              />
            </label>
          </div>

          <p className="px-3 pb-1 pt-2 text-[11px] text-slate-400">
            Vale para todas as telas e continua valendo ao navegar.
          </p>
        </div>
      )}
    </div>
  );
}
